import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { auth0Client, writeAuth0UserContext } from '@/auth/auth0';
import { setStoredAuthProvider } from '@/auth/auth-provider';
import { getRouterInstance } from '@/router/router-instance';
import { ensureRoleScreens } from '@/auth/role-screen-access';
import { CLIENTS_QUERY_KEY, fetchUserClients } from '@/client/use-clients';
import { CLIENT_STORAGE_KEYS, type ClientInfo } from '@/client/client-store';
import { LegacySpinner } from '@/ui/LegacySpinner';

/**
 * Auth0 redirect callback (`/home` default + `/auth0-callback`).
 *
 * Processes the Auth0 authorization response, writes the unified
 * `ey.user.context`, persists `authProvider = 'auth0'`, and returns the user to
 * the originating route (`appState.returnTo`). Unlike the Angular POC it seeds
 * NO static client list — client list + role mapping are resolved via the same
 * dynamic, MSAL-backed bootstrap that `/frameredirect` uses, so the header
 * dropdown and guards behave identically to an MSAL session (PRTL-2857 1d).
 */

function hasAuthCallbackParams(search: string): boolean {
  const p = new URLSearchParams(search);
  return (p.has('code') && p.has('state')) || p.has('error');
}

/** Landing client parity with FrameRedirectPage.resolveLandingClientId. */
function resolveLandingClientId(clients: ClientInfo[], returnTo: string | null): string {
  const deepLinkId = returnTo?.split('?')[0].split('/').filter(Boolean)[0] ?? null;
  if (deepLinkId && clients.some((c) => c.id === deepLinkId)) return deepLinkId;
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent);
  } catch {
    /* storage unavailable */
  }
  if (saved && clients.some((c) => c.id === saved)) return saved;
  return clients[0].id;
}

export function Auth0Callback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const search = window.location.search;

    // A plain visit to the callback path (no Auth0 params) is not a callback —
    // send the user to the shell home instead of stalling.
    if (!hasAuthCallbackParams(search)) {
      navigate({ to: '/', replace: true });
      return;
    }

    const params = new URLSearchParams(search);
    const errParam = params.get('error');
    if (errParam) {
      setError(params.get('error_description') || errParam);
      return;
    }
    if (!auth0Client) {
      setError('Auth0 is not configured.');
      return;
    }

    void (async () => {
      try {
        const result = await auth0Client.handleRedirectCallback();
        const returnTo = (result.appState as { returnTo?: string } | undefined)?.returnTo ?? null;

        const claims = await auth0Client.getIdTokenClaims();
        if (!claims) {
          setError('No identity was returned from Auth0.');
          return;
        }
        writeAuth0UserContext(claims as unknown as Record<string, unknown>);
        setStoredAuthProvider('auth0');

        // Dynamic bootstrap (parity with FrameRedirectPage): resolve the user's
        // clients and warm the landing client's role mapping through the SAME
        // MSAL-backed path so the shell hydrates its store identically.
        let clients: ClientInfo[] = [];
        try {
          clients = await queryClient.fetchQuery({
            queryKey: CLIENTS_QUERY_KEY,
            queryFn: fetchUserClients,
            staleTime: 5 * 60_000,
          });
        } catch {
          /* client fetch failed; useClients() surfaces its own fallback */
        }
        if (cancelled) return;

        let target = returnTo;
        if (clients.length > 0) {
          const landing = resolveLandingClientId(clients, returnTo);
          try {
            await ensureRoleScreens(landing);
          } catch {
            /* guard handles a role-mapping miss on entry */
          }
          if (!target) target = `/${landing}/app-container/home`;
        }
        if (cancelled) return;

        const router = getRouterInstance();
        if (target && router) router.history.replace(target);
        else navigate({ to: '/', replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, queryClient]);

  return (
    <section
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '1rem',
        padding: '2rem',
      }}
    >
      {error ? (
        <>
          <p style={{ margin: 0 }}>{error}</p>
          <button
            type="button"
            className="login-page__btn"
            onClick={() => navigate({ to: '/login', replace: true })}
          >
            Return to sign in
          </button>
        </>
      ) : (
        <>
          <LegacySpinner />
          <div>
            Completing sign-in
            <br />
            You will be redirected in a few seconds....
          </div>
        </>
      )}
    </section>
  );
}
